/**
 * parseJsonField — safely read a value that may be EITHER a JSON string OR an
 * already-deserialized object/array.
 *
 * Why this exists (see PR #227 + the jsonb-parse audit):
 * Postgres `jsonb` columns are deserialized to native JS objects/arrays by the
 * `pg` driver, and the API emits most of them verbatim. A few columns
 * (`theme_json`, `engagement_order_json`, `section_visibility_json`) are
 * stringified by the server before emit. So a given field can arrive as an
 * object OR a string depending on the column — and calling `JSON.parse()` on an
 * object throws (`JSON.parse("[object Object]")` → SyntaxError), which silently
 * blanked real report data on every reload.
 *
 * This helper makes every call site agnostic to which shape the server sent:
 * objects pass through untouched, strings are parsed, and anything malformed or
 * nullish falls back — it never throws. Route ALL reads of jsonb-backed fields
 * through this so the bug class cannot recur if the backend ever changes which
 * columns it stringifies.
 */
export function parseJsonField<T>(
  value: string | object | null | undefined,
  fallback: T,
): T {
  if (value == null) return fallback;
  // jsonb → native object/array: use as-is.
  if (typeof value === 'object') return value as T;
  // Stringified column: parse it. Never throw — fall back on malformed input.
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}
